import React, { useEffect, useState, useMemo } from 'react';
import {
  Table,
  Input,
  Button,
  message,
  Space,
  Card,
  Row,
  Modal,
  Col,
  Typography,
  Select,
  Tag,
  Spin,
} from 'antd';
import { SearchOutlined, DownloadOutlined,EyeOutlined ,EditOutlined, ClearOutlined } from '@ant-design/icons';
import api from '../../api/axios';
import * as XLSX from 'xlsx';

const { Title, Text } = Typography;
const { Option } = Select;

const InventoryList = () => {
  // Data and Loading States
  const [inventory, setInventory] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter States
    const [imageModalVisible, setImageModalVisible] = useState(false);
    const [selectedImage, setSelectedImage] = useState('');
  const [searchText, setSearchText] = useState('');
  const [selectedWarehouse, setSelectedWarehouse] = useState(null); // null means "All Warehouses"

  const [messageApi, contextHolder] = message.useMessage();
const [skuModalOpen, setSkuModalOpen] = useState(false);
const [selectedInventory, setSelectedInventory] = useState(null);
const [newSku, setNewSku] = useState('');
const [updatingSku, setUpdatingSku] = useState(false);

  // Fetch initial data for inventory and warehouses
  const fetchData = async () => {
    setLoading(true);
    try {
      const [invRes, whRes] = await Promise.all([
        api.get('/api/inventories'),
        api.get('/api/warehouses'),
      ]);
      setInventory(invRes.data);
      setWarehouses(whRes.data);
    } catch (err) {
      console.error(err);
      messageApi.error('Failed to fetch required data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);
const openSkuModal = (record) => {
  setSelectedInventory(record);
  setNewSku(record.sku || '');
  setSkuModalOpen(true);
};
const updateSku = async () => {
  if (!newSku.trim()) {
    messageApi.warning('SKU / Location cannot be empty');
    return;
  }

  setUpdatingSku(true);
  try {
    await api.put(`/api/inventories/${selectedInventory.id}`, {
      sku: newSku,
    });

    // Update UI instantly
    setInventory(prev =>
      prev.map(inv =>
        inv.id === selectedInventory.id
          ? { ...inv, sku: newSku }
          : inv
      )
    );

    messageApi.success('Location updated successfully');
    setSkuModalOpen(false);
  } catch (error) {
    console.error(error);
    messageApi.error('Failed to update location');
  } finally {
    setUpdatingSku(false);
  }
};

  // Memoized filtering logic for performance
  const filteredInventory = useMemo(() => {
    let data = inventory;

    // 1. Filter by selected warehouse
    if (selectedWarehouse) {
      data = data.filter(inv => inv.warehouseId === selectedWarehouse);
    }

    // 2. Filter by search text across multiple fields
    const query = searchText.toLowerCase();
    if (query) {
      data = data.filter(inv => {
        const partNumber = inv.sparepart?.partNumber?.toLowerCase() || '';
        const importerId = inv.sparepart?.importerId?.toLowerCase() || '';
        const partName = inv.sparepart?.partName?.toLowerCase() || '';
        const sku = inv.sku?.toLowerCase() || '';
        return (
          partNumber.includes(query) ||
          importerId.includes(query) ||
          partName.includes(query) ||
          sku.includes(query)
        );
      });
    }

    return data;
  }, [searchText, selectedWarehouse, inventory]);
const handleViewImage = async (record) => {
  setSelectedImage(null);              // clear previous
  setImageModalVisible(true);          // open modal immediately
  const spareId = record.sparepart?.id;

  try {
    console.log(`Fetching image for ID: ${spareId}`);
    const res = await api.get(`/api/spareparts/${spareId}/image`);

    console.log('API Response:', res.data);

    if (res.data && res.data.spareImage) {
      const imageSrc = `data:image/jpeg;base64,${res.data.spareImage}`;
      setSelectedImage(imageSrc);
    } else {
      console.warn('No spareImage found in the response.');
      messageApi.warning('No image found for this spare part');
    }
  } catch (error) {
    console.error('Image fetch error:', error);
    messageApi.error('Error fetching spare part image');
  }
};
  // Handle exporting the currently filtered data to Excel
  const handleExport = () => {
    if (!filteredInventory.length) {
      messageApi.warning('No data to export');
      return;
    }

    const dataToExport = filteredInventory.map(inv => ({
      'Warehouse': inv.warehouse?.name || 'N/A',
      'ID': inv.sparepart?.importerId || 'N/A',
      'Part Name': inv.sparepart?.partName || 'N/A',
      'Part Number': inv.sparepart?.partNumber || 'N/A',
      'SKU': inv.sku || 'N/A',
      'Quantity': inv.quantity,
    }));

    try {
      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Inventory');
      XLSX.writeFile(workbook, 'Filtered_Inventory.xlsx');
      messageApi.success('Data exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      messageApi.error('Failed to export data.');
    }
  };

  // Reset all filters to their default state
  const clearFilters = () => {
    setSearchText('');
    setSelectedWarehouse(null);
    messageApi.info('Filters cleared');
  };

  // Define table columns with responsiveness
  const columns = [
    {
      title: 'Warehouse',
      dataIndex: ['warehouse', 'name'],
      key: 'warehouse',
      responsive: ['md'], // Visible on medium screens and up
      render: (name) => <Tag color="blue">{name || 'N/A'}</Tag>,
    },
    {
      title: 'Part Details',
      key: 'partDetails',
      responsive: ['xs'], // Only visible on extra-small screens
      render: (_, record) => (
        <div>
          <Text strong>{record.sparepart?.partName || 'N/A'}</Text>  

          <Text type="secondary">P/N: {record.sparepart?.partNumber || 'N/A'}</Text>  

          <Text type="secondary">ID: {record.sparepart?.importerId || 'N/A'}</Text>
        </div>
      )
    },
     { title: 'ID', dataIndex: ['sparepart', 'importerId'], key: 'importerId' },
    { title: 'Part Name', dataIndex: ['sparepart', 'partName'], key: 'partName', responsive: ['sm'] },
    { title: 'Part Number', dataIndex: ['sparepart', 'partNumber'], key: 'partNumber'}, // Show on large screens
   
    { title: 'Location', dataIndex: 'sku', key: 'sku', responsive: ['sm'] },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      sorter: (a, b) => a.quantity - b.quantity,
      render: (qty) => {
        let color = 'green';
        if (qty < 10) color = 'orange';
        if (qty === 0) color = 'red';
        return <Tag color={color} style={{ fontWeight: 'bold' }}>{qty}</Tag>;
      },
    },
   {
  title: 'Actions',
  key: 'actions',
  render: (_, record) => (
    <Space>
      <Button
        icon={<EyeOutlined />}
        onClick={() => handleViewImage(record)}
      >
        View
      </Button>

      <Button
        icon={<EditOutlined />}
        onClick={() => openSkuModal(record)}
        type="dashed"
      >
        Update Location
      </Button>
    </Space>
  ),
},

  ];

  return (
    <>
      {contextHolder}
      <Card style={{ margin: '16px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
        <Title level={4}>Inventory Overview</Title>
        <Text type="secondary">Search, filter, and export your entire inventory from one place.</Text>

        <Spin spinning={loading}>
          <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
            {/* Search Input */}
            <Col xs={24} md={10}>
              <Input
                placeholder="Search by Part Name, Number, ID, or SKU..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                allowClear
              />
            </Col>

            {/* Warehouse Filter */}
            <Col xs={24} md={8}>
              <Select
                placeholder="Filter by Warehouse"
                value={selectedWarehouse}
                onChange={value => setSelectedWarehouse(value)}
                style={{ width: '100%' }}
                allowClear
              >
                <Option value={null}>All Warehouses</Option>
                {warehouses.map(wh => (
                  <Option key={wh.id} value={wh.id}>{wh.name}</Option>
                ))}
              </Select>
            </Col>

            {/* Action Buttons */}
            <Col xs={24} md={6}>
              <Space direction="horizontal" style={{ width: '100%' }}>
                <Button icon={<ClearOutlined />} onClick={clearFilters}>
                  Clear
                </Button>
                <Button type="primary" icon={<DownloadOutlined />} onClick={handleExport}>
                  Export
                </Button>
              </Space>
            </Col>
          </Row>

          <Table
            style={{ marginTop: '24px' }}
            columns={columns}
            dataSource={filteredInventory}
            rowKey="id"
            pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50'] }}
            scroll={{ x: 'max-content' }}
            loading={loading}
          />
        </Spin>
      </Card>
        <Modal
        open={imageModalVisible}
        title="Spare Part Image"
        footer={null}
        onCancel={() => setImageModalVisible(false)}
        centered
      >
        {selectedImage ? (
          <img
            src={selectedImage}
            alt="Spare Part"
            style={{
              width: '100%',
              borderRadius: 8,
              objectFit: 'contain',
              maxHeight: '70vh',
            }}
          />
        ) : (
          <p style={{ textAlign: 'center', color: '#888' }}>
            Loading image or no image available...
          </p>
        )}
      </Modal>
      <Modal
  open={skuModalOpen}
  title="Update Inventory Location (SKU)"
  onCancel={() => setSkuModalOpen(false)}
  onOk={updateSku}
  confirmLoading={updatingSku}
  okText="Update"
>
  <Text type="secondary">
    Inventory ID: <b>{selectedInventory?.id}</b>
  </Text>

  <Input
    style={{ marginTop: 16 }}
    placeholder="Enter new SKU / Location"
    value={newSku}
    onChange={e => setNewSku(e.target.value)}
  />
</Modal>

    </>
  );
};

export default InventoryList;
